// Supabase client — uses fetch only (no SDK dependency required)
// Needs: NEXT_PUBLIC_SUPABASE_URL, NEXT_PUBLIC_SUPABASE_ANON_KEY

const SUPA_URL  = process.env.NEXT_PUBLIC_SUPABASE_URL
const SUPA_ANON = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

export const isConfigured = () =>
  typeof SUPA_URL === 'string'  && SUPA_URL.startsWith('https://') &&
  typeof SUPA_ANON === 'string' && SUPA_ANON.length > 10

async function request(path, options = {}, token = null) {
  if (!isConfigured()) throw new Error('Supabase not configured')
  const res = await fetch(`${SUPA_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPA_ANON,
      Authorization: `Bearer ${token ?? SUPA_ANON}`,
      ...options.headers,
    },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(
      body?.error_description ?? body?.msg ?? body?.message ?? `Supabase error ${res.status}`
    )
  }
  return body
}

// ── Auth ───────────────────────────────────────────────────────────────────────

export async function authSignUp(email, password) {
  return request('/auth/v1/signup', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

export async function authSignIn(email, password) {
  return request('/auth/v1/token?grant_type=password', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

export async function resendConfirmation(email) {
  return request('/auth/v1/resend', {
    method: 'POST',
    body: JSON.stringify({ type: 'signup', email }),
  })
}

export async function authRefresh(refreshToken) {
  return request('/auth/v1/token?grant_type=refresh_token', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
}

export async function authSignOut(token) {
  return request('/auth/v1/logout', { method: 'POST' }, token).catch(() => {})
}

// ── public.users table ────────────────────────────────────────────────────────

export async function dbGetUser(id, token) {
  const rows = await request(
    `/rest/v1/users?id=eq.${encodeURIComponent(id)}&select=*`,
    { headers: { Accept: 'application/json' } },
    token
  )
  return Array.isArray(rows) ? (rows[0] ?? null) : null
}

export async function dbUpsertUser(row, token) {
  return request('/rest/v1/users', {
    method: 'POST',
    headers: { Prefer: 'resolution=merge-duplicates,return=minimal' },
    body: JSON.stringify(row),
  }, token)
}

// ── User self-service ─────────────────────────────────────────────────────────

/** Update authenticated user's email or password. */
export async function authUpdateUser(data, token) {
  return request('/auth/v1/user', {
    method: 'PUT',
    body: JSON.stringify(data),
  }, token)
}

// ── Saved deals ───────────────────────────────────────────────────────────────

/** Returns an array of deal IDs saved by the user. */
export async function dbGetSavedDeals(userId, token) {
  const rows = await request(
    `/rest/v1/saved_deals?user_id=eq.${encodeURIComponent(userId)}&select=deal_id`,
    { headers: { Accept: 'application/json' } },
    token
  )
  return Array.isArray(rows) ? rows.map((r) => r.deal_id) : []
}

/** Save a deal for the user (idempotent). */
export async function dbSaveDeal(userId, dealId, token) {
  return request('/rest/v1/saved_deals', {
    method: 'POST',
    headers: { Prefer: 'resolution=ignore-duplicates,return=minimal' },
    body: JSON.stringify({ user_id: userId, deal_id: dealId }),
  }, token)
}

/** Remove a saved deal for the user. */
export async function dbUnsaveDeal(userId, dealId, token) {
  return request(
    `/rest/v1/saved_deals?user_id=eq.${encodeURIComponent(userId)}&deal_id=eq.${encodeURIComponent(dealId)}`,
    { method: 'DELETE' },
    token
  )
}

// ── Route alerts ──────────────────────────────────────────────────────────────

/** Returns an array of route alert rows for the user. */
export async function dbGetRouteAlerts(userId, token) {
  const rows = await request(
    `/rest/v1/route_alerts?user_id=eq.${encodeURIComponent(userId)}&select=id,origin,destination&order=created_at.asc`,
    { headers: { Accept: 'application/json' } },
    token
  )
  return Array.isArray(rows) ? rows : []
}

/** Add a route alert for the user. Returns the created row. */
export async function dbAddRouteAlert(userId, origin, destination, token) {
  const rows = await request('/rest/v1/route_alerts', {
    method: 'POST',
    headers: { Prefer: 'resolution=ignore-duplicates,return=representation' },
    body: JSON.stringify({
      user_id:     userId,
      origin:      origin.toUpperCase(),
      destination: destination.toUpperCase(),
    }),
  }, token)
  return Array.isArray(rows) ? (rows[0] ?? null) : (rows ?? null)
}

/** Delete a route alert by id (RLS ensures ownership). */
export async function dbDeleteRouteAlert(alertId, token) {
  return request(
    `/rest/v1/route_alerts?id=eq.${encodeURIComponent(alertId)}`,
    { method: 'DELETE' },
    token
  )
}

// ── Destination watchlists ────────────────────────────────────────────────────

/** Returns array of { id, destination_code } rows for the user. */
export async function dbGetDestinationWatches(userId, token) {
  const rows = await request(
    `/rest/v1/destination_watchlists?user_id=eq.${encodeURIComponent(userId)}&select=id,destination_code&order=created_at.asc`,
    { headers: { Accept: 'application/json' } },
    token
  )
  return Array.isArray(rows) ? rows : []
}

/** Add a destination to the user's watchlist (idempotent). Returns created row or null. */
export async function dbAddDestinationWatch(userId, destinationCode, token) {
  const rows = await request('/rest/v1/destination_watchlists', {
    method: 'POST',
    headers: { Prefer: 'resolution=ignore-duplicates,return=representation' },
    body: JSON.stringify({ user_id: userId, destination_code: destinationCode.toUpperCase() }),
  }, token)
  return Array.isArray(rows) ? (rows[0] ?? null) : (rows ?? null)
}

/** Remove a watchlist entry by id (RLS ensures ownership). */
export async function dbRemoveDestinationWatch(watchId, token) {
  return request(
    `/rest/v1/destination_watchlists?id=eq.${encodeURIComponent(watchId)}`,
    { method: 'DELETE' },
    token
  )
}
